import BasicInput from '@/components/BasicInput';
import WarningPrompt from '@/components/WarningPrompt';

export default {
    components: { BasicInput, WarningPrompt },
    props: ['csrftoken', 'ownerid', 'companyid', 'salesforceid', 'details', 'expiry', 'valid', 'errors'],
    computed: {
        formErrors() {
            return !this.valid && this.errors ? JSON.parse(this.errors) : null;
        },
        powerToleranceLowerValueInvalid() {
            return Number(this.details.powerToleranceLower) > 0;
        }
    },
    data() {
        return {
            powerToleranceLowerError: null,
        }
    },
    methods: {
        getError(field) {
            if (this.formErrors && field in this.formErrors) {
                return 'This field is required';
            }

            if (field === 'powerToleranceLower' && this.powerToleranceLowerValueInvalid) {
                return 'Must be equal to or less than 0';
            }

            return null;
        },
        limitValue(value) {
            if (value > 0) {
                this.powerToleranceLowerError = 'Must be equal to or less than 0';
            } else {
                this.powerToleranceLowerError = null;
            }
        }
    },
    template: `
    <div>
        <form method="POST">
            <input type="hidden" name="action" value="e7project/product" />
            <input type="hidden" name="CRAFT_CSRF_TOKEN" :value="csrftoken" />
            <input type="hidden" name="method" value="update" />
            <input type="hidden" name="ownerId" :value="ownerid" />
            <input type="hidden" name="companyId" :value="companyid" />
            <input type="hidden" name="salesforceId" :value="salesforceid" />

            <WarningPrompt v-if="!valid || powerToleranceLowerValueInvalid" text="This product is missing required fields. The missing fields highlighted below must be completed before this product can be used in the creation of a job." />

            <h3 class="h4 mb-xl">PV Module (STC)</h3>
            <div class="fields">
                <BasicInput label="Power Rating / Power Output" name="details[powerRating]" :value="details.powerRating" :required="true" :error="getError('powerRating')" :readonly="Boolean(details.powerRating)" type="number" step="0.001" unit="W" />
                <BasicInput label="Module Efficiency" name="details[moduleEfficiency]" :value="details.moduleEfficiency" :required="true" :error="getError('moduleEfficiency')" :readonly="Boolean(details.moduleEfficiency)" type="number" step="any" unit="%" min="0" max="100"/>
                <BasicInput label="Power Tolerance - Lower (W)" name="details[powerToleranceLower]" :value="details.powerToleranceLower" :required="true" :error="getError('powerToleranceLower') || powerToleranceLowerError" :readonly="Boolean(details.powerToleranceLower)" type="number" step="0.001" max="0" unit="W" @updated="limitValue" />
                <BasicInput label="Power Tolerance - Upper (W)" name="details[powerToleranceUpper]" :value="details.powerToleranceUpper" :required="true" :error="getError('powerToleranceUpper')" :readonly="Boolean(details.powerToleranceUpper)" type="number" step="0.001" unit="W" />
                <BasicInput label="Voc" name="details[voc]" :value="details.voc" :required="true" :error="getError('voc')" :readonly="Boolean(details.voc)" type="number" step="0.001" unit="V" />
                <BasicInput label="Isc" name="details[panelIsc]" :value="details.panelIsc" :required="true" :error="getError('panelIsc')" :readonly="Boolean(details.panelIsc)" type="number" step="0.001" unit="A" />
                <BasicInput label="Vmp" name="details[vmp]" :value="details.vmp" :required="true" :error="getError('vmp')" :readonly="Boolean(details.vmp)" type="number" step="0.001" unit="V" />
                <BasicInput label="Imp" name="details[imp]" :value="details.imp" :required="true" :error="getError('imp')" :readonly="Boolean(details.imp)" type="number" step="0.001" unit="A" />

                <BasicInput label="Voltage / temp de-rating factor" name="details[vDeratingCelsius]" :value="details.vDeratingCelsius" :required="true" :error="getError('vDeratingCelsius')" :readonly="Boolean(details.vDeratingCelsius)" unit="%/&#8451; or %/&#730;K" type="number" step="0.001" min="-1" max="1" tooltip="%/&#8451; and %/&#730;K are interchangeable values. Negative values must be entered with a '–' symbol" />
                <BasicInput label="Current / temp de-rating factor" name="details[iDerating]" :value="details.iDerating" :required="true" :error="getError('iDerating')" :readonly="Boolean(details.iDerating)" unit="%/&#8451; or %/&#730;K" type="number" step="0.001" min="-1" max="1" tooltip="%/&#8451; and %/&#730;K are interchangeable values. Negative values must be entered with a '–' symbol" />
                <BasicInput label="Power / temp de-rating factor" name="details[pDerating]" :value="details.pDerating" :required="true" :error="getError('pDerating')" :readonly="Boolean(details.pDerating)" unit="%/&#8451; or %/&#730;K" type="number" step="0.001" min="-1" max="1" tooltip="%/&#8451; and %/&#730;K are interchangeable values. Negative values must be entered with a '–' symbol" />

                <BasicInput label="Maximum System Voltage" name="details[maxSystemVoltage]" :value="details.maxSystemVoltage" :required="true" :error="getError('maxSystemVoltage')" :readonly="Boolean(details.maxSystemVoltage)" unit="V" />
                <BasicInput label="Series Fuse rating" name="details[seriesFuseRating]" :value="details.seriesFuseRating" :required="true" :error="getError('seriesFuseRating')" :readonly="Boolean(details.seriesFuseRating)" unit="A" type="number" step="0.001" tooltip="Can sometimes be referred to as overcurrent protection rating or reverse current rating" />
            </div>

            <h3 class="h4 h-padding-lg">General</h3>
            <div class="fields">
                <BasicInput label="Panel Dimensions: Length" name="details[length]" :value="details.length" unit="mm" :readonly="Boolean(details.length)" />
                <BasicInput label="Panel Dimensions: Width" name="details[width]" :value="details.width" unit="mm" :readonly="Boolean(details.width)" />
                <BasicInput label="Panel Dimensions: Height" name="details[height]" :value="details.height" unit="mm" :readonly="Boolean(details.height)" />
                <BasicInput label="Weight" name="details[weight]" :value="details.weight" unit="kg" :readonly="Boolean(details.weight)" />
                <BasicInput label="Connector Manufacturer" name="details[connectorManufacturer]" :value="details.connectorManufacturer" :readonly="Boolean(details.connectorManufacturer)" />
                <BasicInput label="Connector Type" name="details[connectorType]" :value="details.connectorType" :readonly="Boolean(details.connectorType)" />
            </div>

            <h3 class="h4 h-padding-lg">Expiry</h3>
            <div class="fields">
                <BasicInput label="CEC listing expiry date" name="expiry" :value="expiry" :readonly="true" />
            </div>

            <p class="small mt-lg mb-md">While all care has been taken to ensure the My Jobs solution is free from omission and error, the Clean Energy Council takes no responsibility for the use of this information in the design and/or installation of any solar PV system. Please see our <a class="link" href="/legal" target="_blank">Website User Terms and Conditions</a>.</p>

            <button class="btn btn--medium">Update PV Module</button>
        </form>

        <form method="POST">
            <input type="hidden" name="action" value="e7project/product" />
            <input type="hidden" name="CRAFT_CSRF_TOKEN" :value="csrftoken" />
            <input type="hidden" name="method" value="delete" />
            <input type="hidden" name="ownerId" :value="ownerid" />
            <input type="hidden" name="companyId" :value="companyid" />
            <input type="hidden" name="salesforceId" :value="salesforceid" />
            <div class="favourite-product-delete ml-md">
                <button class="btn btn--ghost-thin btn--medium">Delete PV Module</button>
            </div>
         </form>
    </div>
    `,
};
